import {computed, inject, Injectable, signal} from '@angular/core';
import {
  CompanyService,
  CompanySummary,
  ContactService,
  ContactSummary,
  CreateCompanyRequest,
  CreateContactRequest,
  CustomerState,
  UpdateCompanyRequest,
  UpdateContactRequest
} from '../../../../api';
import {lastValueFrom} from 'rxjs';
import {injectMutation, injectQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {QueryKey} from '../../../constants/query-key';

@Injectable({
  providedIn: 'root',
})

export class DirectoryStore {
  private readonly companyService = inject(CompanyService)
  private readonly contactService = inject(ContactService);
  private readonly queryClient = inject(QueryClient);

  companySearchTerm = signal<string | null>(null);
  contactSearchTerm = signal<string | null>(null);

  companyStates = signal<CustomerState[]>([CustomerState.IsActive]);
  contactStates = signal<CustomerState[]>([CustomerState.IsActive]);

  companySummariesGetQuery = injectQuery(() => ({
    queryKey: [QueryKey.COMPANY, this.companySearchTerm(), this.companyStates()],
    queryFn: () => lastValueFrom(this.companyService.getCompanies(this.companySearchTerm(), this.companyStates())),
  }));

  contactSummariesGetQuery = injectQuery(() => ({
    queryKey: [QueryKey.CONTACT, this.contactSearchTerm(), this.contactStates()],
    queryFn: () => lastValueFrom(this.contactService.getContacts(this.contactSearchTerm(), this.contactStates())),
  }));

  createCompanyMutation = injectMutation(() => ({
    mutationFn: (request: CreateCompanyRequest) => lastValueFrom(this.companyService.createCompany(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.COMPANY]});
    },
  }));

  createContactMutation = injectMutation(() => ({
    mutationFn: (request: CreateContactRequest) => lastValueFrom(this.contactService.createContact(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.CONTACT]});
    },
  }));

  updateCompanyMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdateCompanyRequest }) =>
      lastValueFrom(this.companyService.updateCompany(request.id, request.body)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.COMPANY]});
    },
  }));

  deleteCompanyMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.companyService.deleteCompany(id)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.COMPANY]});
    },
  }));

  updateContactMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdateContactRequest }) =>
      lastValueFrom(this.contactService.updateContact(request.id, request.body)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.CONTACT]});
    },
  }));

  deleteContactMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.contactService.deleteContact(id)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.CONTACT]});
    },
  }));

  isDataLoading = computed(() => this.companySummariesGetQuery.isLoading());
  companySummaries = computed<CompanySummary[]>(() => this.companySummariesGetQuery.data()?.companies ?? []);

  isContactsLoading = computed(() => this.contactSummariesGetQuery.isLoading());
  contactSummaries = computed<ContactSummary[]>(() => this.contactSummariesGetQuery.data()?.contacts ?? []);

  refreshIfStale() {
    if (this.companySummariesGetQuery.isStale()) {
      this.companySummariesGetQuery.refetch();
    }
  }

  refreshContactsIfStale() {
    if (this.contactSummariesGetQuery.isStale()) {
      this.contactSummariesGetQuery.refetch();
    }
  }
}
